import { css, SerializedStyles } from '@emotion/react';
import { theme } from '@frontend/theme'; // Added import
import { styles } from '../../styles';
import { FullHeightConfig } from './table-types';

type TableContainerStyleProps = {
  isHoverable: boolean;
  isFocusable: boolean;
  tableId: string;
  leftShadow: boolean;
  rightShadow: boolean;
  topShadow: boolean;
  tableStyle?: SerializedStyles;
  wrapperStyle?: SerializedStyles;
  tableScrollWidth: number;
  tableClientWidth: number;
  tableYPositioning: number;
  fullHeight?: boolean;
  fullHeightConfig?: FullHeightConfig;
  isMobile?: boolean;
  hasHoverableActions?: boolean;
  hasStickyCustomRow?: boolean;
};

export const tableContainerStyle = ({
  isHoverable,
  isFocusable,
  tableStyle,
  tableId,
  wrapperStyle,
  leftShadow,
  rightShadow,
  topShadow,
  tableClientWidth,
  tableScrollWidth,
  tableYPositioning,
  fullHeight,
  fullHeightConfig,
  isMobile,
  hasHoverableActions,
  hasStickyCustomRow,
}: TableContainerStyleProps) => css`
  width: 100%;
  /* height: 100%; */
  display: flex;
  isolation: isolate;
  flex-direction: column;
  ${wrapperStyle};
  @media print {
    height: auto;
  }

  #${tableId}.table {
    border-spacing: 0;
    flex: 1;
    ${fullHeight &&
    !isMobile &&
    `max-height: ${
      fullHeightConfig?.maxHeight
        ? `${fullHeightConfig.maxHeight}px`
        : `calc(100vh - ${tableYPositioning}px - ${theme.spacing(5)})`
    };
      min-height: ${
        fullHeightConfig?.minHeight
          ? `${fullHeightConfig.minHeight}px`
          : `max(calc(100vh - ${tableYPositioning}px - ${theme.spacing(5)}),400px)`
      };
      `}

    @media print {
      max-height: fit-content;
      min-height: auto;
    }

    ${styles.smallScrollbar(theme)}; // Assuming theme can be passed here
    ${tableStyle};
    ${isMobile && 'max-height: unset;'}
    .table__header.resizing {
      background: ${theme.colors.neutral5};
    }

    ${isFocusable === true &&
    `.table__header::after,.table__cell::after {
      content: '';
      width: 100%;
      height: 100%;
      display: block;
      border: 2px solid ${theme.colors.primary50};
      border-radius: inset ${theme.borderRadius.small};
      opacity: 0;
      pointer-events: none;
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      transition: opacity 100ms ease-out;
    }`};

    .table__header,
    .table__cell {
      ${!isMobile &&
      css`
        border-bottom: 1px solid ${theme.colors.neutral20};
      `};
    }

    ${!isMobile &&
    css`
      border: 1px solid ${theme.colors.neutral20};
      border-radius: ${theme.borderRadius.medium};
      .table-row-group:last-of-type .table__row .table__cell {
        border-bottom: 1px solid transparent;
      }
    `}

    ${isHoverable === true &&
    !isMobile &&
    `.table__row:hover:not(.disabled) {
      cursor:pointer;
      .cell__${tableId}.table__cell {
        background: ${theme.colors.neutral5};
      }

      & svg {
        visibility: visible;
      }
    }`};
    .table__row {
      .cell__${tableId}.table__cell.selected {
        background-color: ${theme.colors.neutral5};
      }

      &:hover,
      .cell__${tableId}.table__cell.expanded {
        ${!isMobile &&
        css`
          background: ${theme.colors.neutral5};
        `};
      }

      .cell__${tableId}.table__header.selected {
        background-color: ${theme.colors.neutral5};
      }

      &:hover,
      .cell__${tableId}.table__header.expanded {
        ${!isMobile &&
        css`
          background: ${theme.colors.neutral5};
        `};
      }
    }

    .custom__row__container {
      border-bottom: 1px solid ${theme.colors.neutral20};
    }

    ${hasStickyCustomRow &&
    `.custom__row__sticky {
      width: ${tableClientWidth}px;
      @media print {
        width: 100%;
      }
    }`}

    .expandable__row__container,
    .empty__states__wrapper {
      width: ${tableScrollWidth}px;
    }

    ${!tableScrollWidth &&
    !tableClientWidth &&
    `.empty__states__wrapper{
      display: none;
      animation: intro 0.3s ease;
    }

    `}

    .table__hover__actions {
      display: flex;
      position: absolute;
      left: 0;
      bottom: ${theme.spacing(1)};
      width: ${tableScrollWidth}px;
      z-index: ${theme.zIndex.popover};
      pointer-events: none;
    }

    ${tableScrollWidth &&
    tableClientWidth &&
    `.empty__states__wrapper{
        animation: intro 0.3s ease;
      }`}

    @keyframes intro {
      from {
        opacity: 0;
      }
      to {
        opacity: 1;
      }
    }

    .expandable__row__sticky {
      width: ${tableClientWidth}px;
      border-bottom: 1px solid ${theme.colors.neutral20};
    }

    .empty__state__container {
      width: ${tableClientWidth}px;
    }

    ${isFocusable === true &&
    `.table__header:focus-visible,.table__cell:focus-visible {
      outline: none;
      ::after {
        opacity: 1;
      }
    }`};

    &.sticky {
      overflow: auto;
      @media print {
        overflow: visible;
      }
      .header,
      .footer {
        position: sticky;
        ${!hasHoverableActions && `z-index: 1;`}
      }

      .header {
        top: 0;
        display: flex;
      }

      .table__body {
        position: relative;
        z-index: 0;
        display: grid;
      }

      .table__header {
        position: sticky !important;
      }

      .thead {
        transition: 0.3s ease;
      }

      ${topShadow &&
      `& .thead.headers__${tableId} {
        box-shadow: 0 4px 4px 0 rgba(49, 53, 61, 0.19);
      }`}

      [data-sticky-id] {
        position: sticky;
        box-shadow: ${theme.shadows.light};
      }

      .cell__${tableId}[data-sticky-last-left-td]::before {
        position: absolute;
        top: 0;
        right: 0;
        bottom: -1px;
        width: ${theme.spacing(4)};
        transform: translate(100%);
        transition: opacity 0.3s;
        content: '';
        pointer-events: none;
        box-shadow: ${theme.shadows.innerLeft};
        opacity: 0;
      }

      ${leftShadow &&
      `.cell__${tableId}[data-sticky-last-left-td]::before {
        opacity: 1;
      }`}

      .cell__${tableId}[data-sticky-first-right-td]::before {
        position: absolute;
        top: 0;
        left: -${theme.spacing(8)};
        bottom: -1px;
        width: ${theme.spacing(4)};
        transform: translate(100%);
        transition: opacity 0.3s;
        content: '';
        pointer-events: none;
        box-shadow: ${theme.shadows.innerRight};
        opacity: 0;
      }

      ${rightShadow &&
      `.cell__${tableId}[data-sticky-first-right-td]::before {
        opacity: 1;
      }`}
    }
  }
`;

type TableCellStylesProps = {
  isMobile?: boolean;
  isDisabled?: boolean;
};

const tableCellStyles = ({ isMobile, isDisabled }: TableCellStylesProps) => {
  return [
    css`
      position: relative;
      display: flex;
      align-items: center;
      color: ${isDisabled ? theme.font.colors.disabled : theme.font.colors.default};
      font-size: ${theme.fontSize(14)};
      font-weight: ${theme.font.weight.regular};
      background-color: ${theme.colors.white};
      outline: none;
    `,
    !isMobile &&
      css`
        padding: ${theme.spacing(1, 2)};
        min-height: 52px;
      `,
  ];
};

type TableHeaderStylesProps = {
  canSort: boolean;
  isMobile: boolean;
};

const tableHeaderStyles = ({ canSort, isMobile }: TableHeaderStylesProps) => {
  return css`
    position: relative;
    color: ${theme.colors.neutral50};
    font-size: ${theme.fontSize(14)};
    font-weight: ${theme.font.weight.regular};
    padding: ${theme.spacing(0.5, 2)};
    min-height: 52px;
    user-select: none;
    background-color: ${theme.colors.white};
    outline: none;
    :not(:last-child) {
      border-right: 1px solid ${theme.colors.neutral20};
    }

    :last-child {
      border-right: 1px solid transparent;
    }

    cursor: default;
    ${canSort &&
    !isMobile &&
    `
    &:hover{
      background-color: ${theme.colors.neutral5};
    }
    `}
  `;
};

type TableMobileRowActionsStylesProps = {
  isExpanded: boolean;
  isSelected: boolean;
};

const mobileRowActionsStyles = ({ isExpanded, isSelected }: TableMobileRowActionsStylesProps) => css`
  display: flex;
  justify-content: flex-end;
  width: 100%;
  min-height: ${theme.spacing(5)};
  align-items: center;
  padding: ${theme.spacing(1)};
  border-radius: ${theme.spacing(0.5)};
  background-color: ${isSelected ? theme.colors.neutral5 : isExpanded ? theme.colors.neutral5 : theme.colors.neutral5};
`;

const rowCardContainerStyles = css`
  flex-direction: column;
  border-bottom: 1px solid ${theme.colors.neutral20};
  padding: ${theme.spacing(1, 0)};
`;

const rowCardStyles = css`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: ${theme.spacing(0.5)};
  grid-auto-flow: dense;
`;

type TableFilterStylesProps =
  | {
      type: 'filterSidebarHeader' | 'filterSectionHeader' | 'filterSidebarButtons' | 'filterSidebarFooter';
    }
  | {
      type: 'filterSection';
      extra?: boolean;
    };

const tableFilterStyles = (props: TableFilterStylesProps) => {
  const { type } = props;

  switch (type) {
    case 'filterSection':
      return css`
        padding: ${theme.spacing(2, 4, 2, 4)};
        &:not(:first-of-type) {
          border-top: 1px solid ${theme.colors.neutral20};
        }
        ${props.extra === true &&
        `
        margin-bottom: ${theme.spacing(2)};
        `}
      `;
    case 'filterSidebarHeader':
      return css`
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: ${theme.spacing(2)};
        position: sticky;
        background: white;
        z-index: ${theme.zIndex.high};
        border-bottom: 1px solid ${theme.colors.neutral20};
        top: 0;
      `;
    case 'filterSectionHeader':
      return css`
        display: flex;
        justify-content: space-between;
        margin: ${theme.spacing(2, 0)};
      `;
    case 'filterSidebarButtons':
      return css`
        flex: 0 1;
      `;
    case 'filterSidebarFooter':
      return css`
        border-top: 1px solid ${theme.colors.neutral20};
        padding: ${theme.spacing(2)};
        display: grid;
        gap: ${theme.spacing(2)};
      `;
    default:
      return;
  }
};

const filterNotificationBadgeStyles = css`
  ::after {
    position: absolute;
    background: ${theme.colors.primary50};
    border-radius: 16px;
    border: 1.5px solid ${theme.colors.white};
    height: ${theme.spacing(1.5)};
    width: ${theme.spacing(1.5)};
    top: 6px;
    left: ${theme.spacing(3)};
    z-index: ${theme.zIndex.high};
    content: '';
    transition: border-color, opacity ease-in-out 250ms;
  }
  :hover::after,
  :focus::after {
    border-color: ${theme.colors.neutral10};
  }
`;

const filterColumnStyles = css`
  border-bottom: 1px solid ${theme.colors.neutral10};
  padding: ${theme.spacing(1, 2)};
  min-height: 45px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

type SortingCaretTypes = { isSorted: boolean; isSortedDesc?: boolean };

const sortingCaretStyles = ({ isSorted, isSortedDesc }: SortingCaretTypes) => {
  return [
    css`
      margin-left: ${theme.spacing(1)};
      color: ${theme.colors.neutral20};

      svg {
        transition: color 0.3s ease;
      }
    `,
    isSorted &&
      (!isSortedDesc
        ? css`
            svg:first-of-type {
              color: ${theme.colors.neutral60};
            }
          `
        : css`
            svg:nth-of-type(2) {
              color: ${theme.colors.neutral60};
            }
          `),
  ];
};

const hoverRowActionsStyles = css`
  display: flex;
  position: sticky;
  right: ${theme.spacing(3)};
  cursor: default;
  align-items: flex-start;
  pointer-events: auto;
  background: ${theme.colors.white};
  padding: ${theme.spacing(1)};
  gap: ${theme.spacing(1)};
  border-radius: ${theme.borderRadius.small};
  box-shadow: ${theme.shadows.floating};
`;

const columnResizerStyles = css`
  width: 9px;
  height: 100%;
  position: absolute;
  right: 0;
  top: 0;
  transform: translateX(50%) translateZ(1px);
  touch-action: none;
  transition: background 0.2s ease;

  &::after {
    content: '';
    position: absolute;
    left: 3px;
    top: 0;
    width: 2px;
    height: 100%;
    opacity: 0;
    background: ${theme.colors.neutral70};
    transition: opacity 0.2s ease;
  }

  &:hover::after {
    opacity: 1;
  }

  &:active::after {
    opacity: 1;
  }
`;

const reorderColumnWhileTapStyles = {
  cursor: 'grabbing',
  boxShadow: theme.shadows.floating,
  background: theme.colors.neutral10,
};

export const TableCommonStyles = {
  tableContainerStyles: (props: TableContainerStyleProps) => tableContainerStyle(props),
  tableCellStyles: (props: TableCellStylesProps) => tableCellStyles(props),
  tableHeaderStyles: (props: TableHeaderStylesProps) => tableHeaderStyles(props),
  mobileRowActionsStyles: (props: TableMobileRowActionsStylesProps) => mobileRowActionsStyles(props),
  rowCardStyles: rowCardStyles,
  rowCardContainerStyles: rowCardContainerStyles,
  hoverRowActionsStyles: hoverRowActionsStyles,
  columnResizerStyles: columnResizerStyles,
  sortingCaretStyles: (props: SortingCaretTypes) => sortingCaretStyles(props),
  tableFilterStyles: (props: TableFilterStylesProps) => tableFilterStyles(props),
  filterNotificationBadgeStyles: filterNotificationBadgeStyles,
  filterColumnStyles: filterColumnStyles,
  reorderColumnWhileTapStyles: reorderColumnWhileTapStyles,
};
